import * as React from 'react';
import { MouseEvent, ReactElement, ReactNode, useContext } from 'react';
import { Button } from 'primereact/button';
import { NavigateFunction, useNavigate } from 'react-router';
import { IntlShape, useIntl } from 'react-intl';
import { Authorizer } from '../../Auth/utils/Authorizer';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { Validation } from '../../util/validation/interfaces';

export interface FormContentProps {
  children: ReactNode[];
  validation: Validation;
  onSave: () => void;
  saving: boolean;
  onCancel?: () => void;
  prev?: (event: MouseEvent) => void;
  next?: (event: MouseEvent) => void;
  isNew: boolean;
}

export function FormContent(props: FormContentProps): ReactNode {
  const authorizer: Authorizer = useContext(AuthContext).authorizer;
  const navigate: NavigateFunction = useNavigate();
  const intl: IntlShape = useIntl();

  const onCancel = (event: MouseEvent): void => {
    event.preventDefault();
    if (props.onCancel) {
      void props.onCancel();
      return;
    }
    if (window.history?.length && window.history.length > 1) {
      void navigate(-1);
      return;
    }
    void navigate('/');
  };

  const onSubmit = (event: MouseEvent): void => {
    event.preventDefault();
    props.onSave();
  };

  if(props.children.length < 0) return <></>;

  const classNames: string[] = ['col-12', 'flex', 'flex-column', 'gap-2', 'mx-auto'];
  if(props.children.length > 1) {
    classNames.push('xl:col-6');
  }

  return (
    <div className={'grid px-3'} style={{ maxWidth: props.children.length > 1 ? '100rem' : '50rem' }}>
      {props.children.map(
        (child: ReactElement, idx: number): ReactNode => (
          <div key={idx} className={classNames.join(' ')} style={{ maxWidth: '50rem' }}>
            {child}
          </div>
        )
      )}
      <div className={'col-12 flex gap-2 justify-content-end'}>
        <Button
          label={intl.formatMessage({ id: 'button.submit' })}
          onClick={onSubmit}
          disabled={!props?.validation?.valid || props.saving}
          icon={props.saving ? 'pi pi-spin pi-spinner' : undefined}
        ></Button>
        <Button
          label={intl.formatMessage({ id: 'button.abort' })}
          onClick={onCancel}
          severity={'warning'}
          outlined={true}
        ></Button>
        {props.prev !== undefined && (
          <Button
            label={'<<'}
            tooltip={intl.formatMessage({ id: 'button.prevPage' })}
            tooltipOptions={{ position: 'bottom' }}
            disabled={props.prev === null}
            onClick={props.prev}
          />
        )}
        {props.next !== undefined && (
          <Button
            label={'>>'}
            tooltip={intl.formatMessage({ id: 'button.nextPage' })}
            tooltipOptions={{ position: 'bottom' }}
            disabled={props.next === null}
            onClick={props.next}
          />
        )}
        {!props.isNew && authorizer.hasAnyOfRoles(['ROLE_EDITOR']) && (
          <Button icon={'pi pi-trash'} severity={'warning'} outlined={true}></Button>
        )}
        {!props.isNew && authorizer.hasAnyOfRoles(['ROLE_ADMIN']) && (
          <Button icon={'pi pi-trash'} severity={'danger'} outlined={true}></Button>
        )}
      </div>
    </div>
  );
}
